import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ARTICLES, HELP_CATEGORIES, ARTICLE_ORDER, HELP_VERSION } from '../lib/help_content';
import * as Icons from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Section renderers ─────────────────────────────────────────
function InlineMd({ text }) {
  // Minimal inline formatter: **bold** and `code`
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;
  const push = (node) => parts.push(<React.Fragment key={key++}>{node}</React.Fragment>);
  while (remaining) {
    const bMatch = remaining.match(/^\*\*([^*]+)\*\*/);
    const cMatch = remaining.match(/^`([^`]+)`/);
    if (bMatch) {
      push(<strong className="font-semibold text-slate-900">{bMatch[1]}</strong>);
      remaining = remaining.slice(bMatch[0].length);
      continue;
    }
    if (cMatch) {
      push(<code className="px-1 py-0.5 rounded bg-slate-100 text-[13px] text-slate-800 font-mono">{cMatch[1]}</code>);
      remaining = remaining.slice(cMatch[0].length);
      continue;
    }
    // Take chars until next ** or `
    const next = remaining.search(/(\*\*|`)/);
    if (next === -1) { push(remaining); remaining = ""; }
    else { push(remaining.slice(0, next)); remaining = remaining.slice(next); }
  }
  return <>{parts}</>;
}

function Section({ section, nav }) {
  switch (section.type) {
    case "summary":
      return <div className="mb-6 text-[15px] text-slate-700 leading-relaxed italic border-l-2 border-[#679286]/60 pl-4"><InlineMd text={section.text}/></div>;
    case "h2":
      return <h2 className="font-serif text-2xl text-slate-900 mt-8 mb-3">{section.text}</h2>;
    case "h3":
      return <h3 className="font-serif text-lg text-slate-800 mt-6 mb-2">{section.text}</h3>;
    case "p":
      return <p className="text-[15px] text-slate-700 leading-relaxed mb-3"><InlineMd text={section.text}/></p>;
    case "bullets":
      return (
        <ul className="text-[15px] text-slate-700 space-y-1.5 mb-4 pl-4">
          {section.items.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-[#679286] shrink-0 mt-1.5">•</span>
              <span className="flex-1"><InlineMd text={it}/></span>
            </li>
          ))}
        </ul>
      );
    case "numbered":
      return (
        <ol className="text-[15px] text-slate-700 space-y-1.5 mb-4 list-decimal pl-6">
          {section.items.map((it, i) => <li key={i}><InlineMd text={it}/></li>)}
        </ol>
      );
    case "try_live":
      return (
        <button
          onClick={() => nav(section.href)}
          data-testid={`help-try-live-${section.href.replace(/\W+/g,'-')}`}
          className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#679286] text-white text-sm font-semibold hover:bg-[#4a6d63] transition">
          <Icons.ArrowRight size={14}/> {section.label || "Try it live"}
        </button>
      );
    case "field_table":
      return (
        <div className="mb-5">
          {section.title && <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">{section.title}</div>}
          <div className="rounded-2xl overflow-hidden border border-slate-200">
            <table className="w-full text-[14px]">
              <tbody>
                {section.rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-white" : "bg-slate-50/50"}>
                    <td className="py-2.5 px-3 font-medium text-slate-900 align-top w-1/3"><InlineMd text={r.name}/></td>
                    <td className="py-2.5 px-3 text-slate-700 leading-relaxed"><InlineMd text={r.meaning}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    case "callout": {
      const styles = {
        info: "bg-blue-50 border-blue-200 text-blue-900",
        warning: "bg-amber-50 border-amber-200 text-amber-900",
        danger: "bg-red-50 border-red-200 text-red-900",
      }[section.variant] || "bg-slate-50 border-slate-200 text-slate-800";
      const IconMap = { info: Icons.Info, warning: Icons.AlertTriangle, danger: Icons.OctagonAlert };
      const I = IconMap[section.variant] || Icons.Info;
      return (
        <div className={`mb-5 rounded-2xl p-4 border ${styles} flex gap-3 items-start`}>
          <I size={18} className="shrink-0 mt-0.5"/>
          <div className="text-[14px] leading-relaxed flex-1"><InlineMd text={section.text}/></div>
        </div>
      );
    }
    case "faq":
      return (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">Frequently asked</div>
          <div className="space-y-2">
            {section.qa.map((qa, i) => (
              <details key={i} className="group rounded-2xl border border-slate-200 bg-white p-3 open:bg-slate-50/70" data-testid={`help-faq-${i}`}>
                <summary className="cursor-pointer text-sm font-medium text-slate-900 flex justify-between items-center gap-2">
                  <span>{qa.q}</span>
                  <Icons.ChevronDown size={14} className="text-slate-400 group-open:rotate-180 transition"/>
                </summary>
                <div className="mt-2 text-[14px] text-slate-700 leading-relaxed"><InlineMd text={qa.a}/></div>
              </details>
            ))}
          </div>
        </div>
      );
    case "related":
      return (
        <div className="mb-4 mt-8 pt-4 border-t border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">Related articles</div>
          <div className="flex flex-wrap gap-2">
            {section.slugs.filter(s => ARTICLES[s]).map(s => {
              const a = ARTICLES[s];
              return (
                <Link key={s} to={`/app/help/${s}`}
                  data-testid={`help-related-${s}`}
                  className="px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs text-slate-700 hover:bg-slate-50 hover:border-[#679286] transition">
                  {a.title} →
                </Link>
              );
            })}
          </div>
        </div>
      );
    case "code":
      return (
        <pre className="mb-4 rounded-xl bg-slate-900 text-white p-4 text-[13px] font-mono overflow-x-auto"><code>{section.text}</code></pre>
      );
    default:
      return null;
  }
}

// ─── Sidebar ───────────────────────────────────────────────────
function HelpSidebar({ currentSlug, search, setSearch }) {
  const q = (search || "").trim().toLowerCase();
  return (
    <aside className="w-full lg:w-72 shrink-0 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
      <div className="glass p-4 lg:p-5 rounded-3xl">
        <div className="relative mb-3">
          <Icons.Search size={14} className="absolute left-3 top-3 text-slate-400"/>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search help…"
            data-testid="help-search"
            className="w-full pl-8 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
          />
        </div>
        <nav className="space-y-4">
          {HELP_CATEGORIES.map(cat => {
            const slugs = cat.slugs.filter(s => {
              const a = ARTICLES[s];
              if (!a) return false;
              if (!q) return true;
              return (
                a.title.toLowerCase().includes(q) ||
                (a.subtitle || "").toLowerCase().includes(q) ||
                cat.label.toLowerCase().includes(q)
              );
            });
            if (slugs.length === 0) return null;
            return (
              <div key={cat.key}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 font-semibold px-2">{cat.label}</div>
                <div className="space-y-0.5">
                  {slugs.map(s => {
                    const a = ARTICLES[s];
                    const active = currentSlug === s;
                    return (
                      <Link key={s} to={`/app/help/${s}`}
                        data-testid={`help-nav-${s}`}
                        className={`block px-2 py-1.5 rounded-lg text-sm transition ${
                          active ? 'bg-[#679286] text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'
                        }`}>
                        {a.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="mt-6 pt-4 border-t border-slate-200 text-[10px] text-slate-400 px-2">
          Version {HELP_VERSION}
        </div>
      </div>
    </aside>
  );
}

// ─── Article renderer ──────────────────────────────────────────
function Article({ slug }) {
  const nav = useNavigate();
  const article = ARTICLES[slug];
  if (!article) {
    return (
      <div className="glass p-12 text-center text-slate-500 rounded-3xl">
        <Icons.HelpCircle size={40} className="mx-auto mb-3 text-slate-300"/>
        <div className="font-serif text-2xl">Article not found</div>
        <div className="text-sm mt-1">Pick a topic from the sidebar or go back to <Link to="/app/help" className="text-[#679286] underline">Help home</Link>.</div>
      </div>
    );
  }
  const IconEl = Icons[article.icon] || Icons.BookOpen;
  const idx = ARTICLE_ORDER.indexOf(slug);
  const prev = idx > 0 ? ARTICLE_ORDER[idx - 1] : null;
  const next = idx >= 0 && idx < ARTICLE_ORDER.length - 1 ? ARTICLE_ORDER[idx + 1] : null;

  return (
    <motion.div
      key={slug}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="glass p-6 lg:p-8 rounded-3xl">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#679286] to-[#4a6d63] grid place-items-center shrink-0 shadow-lg">
          <IconEl size={22} className="text-white"/>
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
            {article.read_time_min ? `${article.read_time_min} min read` : ""}
            {article.updated_at && <span>· Updated {article.updated_at}</span>}
          </div>
          <h1 className="font-serif text-3xl text-slate-900 mt-0.5" data-testid={`help-article-${slug}`}>{article.title}</h1>
          {article.subtitle && <div className="text-sm text-slate-500 mt-1">{article.subtitle}</div>}
        </div>
      </div>
      <div className="prose max-w-none">
        {(article.sections || []).map((sec, i) => <Section key={i} section={sec} nav={nav}/>)}
      </div>
      {(prev || next) && (
        <div className="mt-8 pt-4 border-t border-slate-200 flex items-center justify-between gap-2">
          {prev ? (
            <Link to={`/app/help/${prev}`} className="text-sm text-slate-600 hover:text-[#679286] inline-flex items-center gap-1">
              <Icons.ArrowLeft size={14}/> {ARTICLES[prev].title}
            </Link>
          ) : <span/>}
          {next ? (
            <Link to={`/app/help/${next}`} className="text-sm text-slate-600 hover:text-[#679286] inline-flex items-center gap-1 text-right">
              {ARTICLES[next].title} <Icons.ArrowRight size={14}/>
            </Link>
          ) : <span/>}
        </div>
      )}
    </motion.div>
  );
}

// ─── Landing (no slug) ─────────────────────────────────────────
function HelpHome() {
  return (
    <div className="glass p-6 lg:p-8 rounded-3xl">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#679286] to-[#4a6d63] grid place-items-center shrink-0 shadow-lg">
          <Icons.BookOpen size={22} className="text-white"/>
        </div>
        <div>
          <h1 className="font-serif text-3xl text-slate-900">Iris Help Center</h1>
          <div className="text-sm text-slate-500 mt-1">Every feature explained. Detailed reference + FAQs. Kept up to date on every release.</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {HELP_CATEGORIES.map(cat => (
          <div key={cat.key} className="rounded-2xl bg-white border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{cat.label}</div>
            <div className="space-y-1">
              {cat.slugs.map(s => ARTICLES[s] && (
                <Link key={s} to={`/app/help/${s}`}
                  className="block text-sm text-slate-700 hover:text-[#679286] hover:underline">
                  {ARTICLES[s].title}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl p-4 bg-slate-50/60 border border-slate-200 text-sm text-slate-600">
        <div className="font-medium text-slate-800 mb-1 flex items-center gap-1.5">
          <Icons.Sparkles size={14} className="text-[#679286]"/> Start here
        </div>
        Read <Link to="/app/help/overview" className="text-[#679286] underline font-medium">Welcome to Iris Realty</Link> for a 3-minute tour, then dive into <Link to="/app/help/deal-radar" className="text-[#679286] underline font-medium">Deal Radar</Link>.
      </div>
    </div>
  );
}

// ─── Page entry ────────────────────────────────────────────────
export default function Help() {
  const { slug } = useParams();
  const [search, setSearch] = useState("");

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-8" data-testid="help-page">
      <div className="flex flex-col lg:flex-row gap-6">
        <HelpSidebar currentSlug={slug} search={search} setSearch={setSearch}/>
        <main className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            {slug ? <Article slug={slug}/> : <HelpHome/>}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
